import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";

const STATUS = [["available", "Available now"], ["coming_soon", "Coming soon"], ["hidden", "Hidden"]];

function MethodRow({ label, value, onValue, placeholder, status, onStatus, testId }) {
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_170px]" data-testid={`${testId}-row`}>
      <div><Label>{label}</Label><Input value={value} placeholder={placeholder} onChange={(e) => onValue(e.target.value)} data-testid={testId} /></div>
      <div><Label>Status</Label>
        <select value={status} onChange={(e) => onStatus(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid={`${testId}-status`}>
          {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></div>
    </div>
  );
}

export function PaymentMethodsSettings() {
  const { data, reload } = useApi("/settings/payment");
  const [p, setP] = useState(null);
  const [preview, setPreview] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { if (data) { setP(data); setPreview(data.guidance?.text || ""); } }, [data]);
  const save = async () => {
    try {
      const { data: out } = await api.put("/admin/settings/payment", {
        zelle: p.zelle, cashapp: p.cashapp, check_payable_to: p.check_payable_to, zelle_status: p.zelle_status,
        cashapp_status: p.cashapp_status, check_status: p.check_status, cash_at_meeting: p.cash_at_meeting, reminder_note: p.reminder_note });
      setPreview(out.guidance.text); toast.success("Payment methods saved — reminders and the portal now use this wording"); reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  if (!p) return null;
  const set = (k) => (v) => setP((s) => ({ ...s, [k]: v }));
  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/admin/dues/reminder/test"); toast.success(`Sample reminder sent to ${data.sent_to}${data.sample_figures ? " (illustrative figures — you are paid up)" : ""}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setTesting(false); }
  };
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="payment-methods-settings">
      <div>
        <h3 className="font-serif-display text-xl font-bold text-crimson">Payment methods</h3>
        <p className="text-xs text-muted-foreground">Controls what members see on the Dues page and the "how to pay" advice in every dues reminder, arrears statement and PDF. Update any time — changes apply immediately.</p>
      </div>
      <MethodRow label="Cash App $Cashtag" value={p.cashapp} onValue={set("cashapp")} placeholder="$OkuapemmanMa" status={p.cashapp_status} onStatus={set("cashapp_status")} testId="pay-cashapp" />
      <MethodRow label="Zelle email / phone" value={p.zelle} onValue={set("zelle")} placeholder="treasurer@…" status={p.zelle_status} onStatus={set("zelle_status")} testId="pay-zelle" />
      <MethodRow label="Checks payable to" value={p.check_payable_to} onValue={set("check_payable_to")} status={p.check_status} onStatus={set("check_status")} testId="pay-check" />
      <div className="flex items-center justify-between rounded-lg border border-gold/30 bg-parchment-card px-3 py-2">
        <Label htmlFor="pay-cash-meeting">Accept cash at the next general meeting</Label>
        <Switch id="pay-cash-meeting" checked={p.cash_at_meeting} onCheckedChange={set("cash_at_meeting")} data-testid="pay-cash-meeting" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-dashed border-gold/40 px-3 py-2 text-sm">
        <span>Online card payments</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.online_payments ? "bg-palm/15 text-palm" : "bg-gold/20 text-crimson"}`} data-testid="pay-online-status">{p.online_payments ? "Available" : "Coming soon"}</span>
      </div>
      <div><Label>Extra note for reminders (optional)</Label>
        <Textarea rows={2} value={p.reminder_note} onChange={(e) => set("reminder_note")(e.target.value)} placeholder="e.g. Please include your member ID in the Cash App note." data-testid="pay-reminder-note" /></div>
      <div className="rounded-lg bg-gold/10 p-3 text-sm" data-testid="pay-guidance-preview">
        <p className="eyebrow text-gold">What members will read</p>
        <p className="mt-1">{preview}</p>
      </div>
      <Button onClick={save} className="w-full bg-crimson" data-testid="pay-save">Save payment methods</Button>
      <Button variant="outline" onClick={sendTest} disabled={testing} className="w-full gap-2 border-gold text-crimson" data-testid="pay-test-reminder"><Send size={15} /> {testing ? "Sending…" : "Send me a test dues reminder"}</Button>
      <p className="text-[11px] text-muted-foreground">Save first, then send — the test uses the saved wording and goes only to your own email.</p>
    </div>
  );
}

export function EmailSenderSettings() {
  const { data, reload } = useApi("/admin/settings/email-sender");
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  const save = async () => {
    try { await api.put("/admin/settings/email-sender", { from_name: s.from_name, reply_to: s.reply_to, paused: !!s.paused, test_mode: !!s.test_mode, test_recipient: s.test_recipient }); toast.success(s.paused ? "Outgoing email PAUSED — nothing will be sent until you resume" : "Email sender saved"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  if (!s) return null;
  return (
    <div className="glass rounded-2xl p-6 space-y-3" data-testid="email-sender-settings">
      <h3 className="font-serif-display text-xl font-bold text-crimson">Email sender</h3>
      <p className="text-xs text-muted-foreground">All emails are sent from <span className="font-mono-accent">{s.from_email}</span>. Set the display name and where member replies should go.</p>
      <div><Label>From name</Label><Input value={s.from_name} onChange={(e) => setS({ ...s, from_name: e.target.value })} data-testid="email-from-name" /></div>
      <div><Label>Reply-to address</Label><Input type="email" value={s.reply_to} onChange={(e) => setS({ ...s, reply_to: e.target.value })} placeholder="info@okuapemmanma.org" data-testid="email-reply-to" /></div>
      <label className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${s.paused ? "border-crimson bg-crimson/10" : "border-gold/30 bg-parchment-card"}`}>
        <span><b>Pause all outgoing email</b><br /><span className="text-xs text-muted-foreground">Safety switch — e.g. while republishing or testing. Reminders, statements and notices are skipped (and logged) until you turn this off.</span></span>
        <Switch checked={!!s.paused} onCheckedChange={(v) => setS({ ...s, paused: v })} data-testid="email-paused" />
      </label>
      {s.paused && <p className="text-xs font-semibold text-crimson" data-testid="email-paused-banner">Outgoing email is currently PAUSED.</p>}
      <label className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${s.test_mode ? "border-amber-500 bg-amber-50" : "border-gold/30 bg-parchment-card"}`}>
        <span><b>Test mode — deliver only to the test address</b><br /><span className="text-xs text-muted-foreground">While on, every email (and SMS) to anyone other than the test address is skipped and logged, so real members are never confused during testing.</span></span>
        <Switch checked={!!s.test_mode} onCheckedChange={(v) => setS({ ...s, test_mode: v })} data-testid="email-test-mode" />
      </label>
      {s.test_mode && <div><Label>Test address (the only inbox that receives mail)</Label><Input type="email" value={s.test_recipient} onChange={(e) => setS({ ...s, test_recipient: e.target.value })} data-testid="email-test-recipient" /></div>}
      <Button onClick={save} className="w-full bg-crimson" data-testid="email-sender-save">Save email sender</Button>
    </div>
  );
}
